import {
  Cancel as CancelIcon,
  Comment as CommentIcon,
  Edit as EditIcon,
  SwapHoriz as ReplaceIcon,
  Save as SaveIcon,
  Undo as UndoIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { Box, Button, IconButton, Stack, TextField } from '@mui/material';
import AdminUser from 'AdminUser';
import React from 'react';
import ContentManagementInterface from '../../models/ContentManagementInterface';
import {
  ContentReviewInterface,
  ReviewSectionConversation,
} from '../../models/ContentReviewInterface';
import { Palette } from 'Palette';
import { StyledCard, StyledCardContent, StyledTypography } from '../styles/styles';
import CommentIconComponent from './CommentIcon';
import ReviewContentEditItemRenderer from './ReviewContentEditItemRenderer';
import { getSectionTitle } from './reviewContentEditViewUtils';

interface ReviewContentEditSectionProps {
  item: any;
  index: number;
  tone: keyof typeof Palette;
  modelType: 'FundOpportunity' | 'FundUpdate';
  draftContent: ContentManagementInterface;
  reviewData: ContentReviewInterface;
  currentUser: AdminUser;
  editingSectionId: string | null;
  editedContent: any;
  submitting: boolean;
  showMinimalReply: string | null;
  minimalReplyText: string;
  onEditStart: (sectionId: string, originalContent: any) => void;
  onEditCancel: () => void;
  onEditSave: (sectionId: string, originalContent: any) => void;
  onRevertToDraft: (sectionId: string) => void;
  onReplaceComponent: (sectionId: string, currentContent: any) => void;
  onViewOriginalComponent: (sectionId: string) => void;
  onEditedContentChange: (content: any) => void;
  onShowMinimalReply: (sectionId: string | null) => void;
  onMinimalReplyTextChange: (text: string) => void;
  onMinimalReplySubmit: (sectionId: string) => void;
  onMinimalReplyCancel: () => void;
  onReviewDataUpdate: (updatedData: ContentReviewInterface) => Promise<void>;
}

export default function ReviewContentEditSection({
  item,
  index,
  tone,
  modelType,
  draftContent,
  reviewData,
  currentUser,
  editingSectionId,
  editedContent,
  submitting,
  showMinimalReply,
  minimalReplyText,
  onEditStart,
  onEditCancel,
  onEditSave,
  onRevertToDraft,
  onReplaceComponent,
  onViewOriginalComponent,
  onEditedContentChange,
  onShowMinimalReply,
  onMinimalReplyTextChange,
  onMinimalReplySubmit,
  onMinimalReplyCancel,
  onReviewDataUpdate,
}: ReviewContentEditSectionProps) {
  const sectionId = `section-${index}`;
  const isEditing = editingSectionId === sectionId;
  const change = reviewData.changes.find((c) => c.sectionId === sectionId);
  const sectionComments = reviewData.comments.filter((c) => c.sectionId === sectionId);
  const displayContent = change?.changed && change.accepted ? change.contentAfter : item;
  const currentContent = isEditing ? editedContent : displayContent;
  const isNewSection = change?.changed && !change.contentBefore;

  const handleReply = async (conversationId: string, message: string) => {
    try {
      const existingConversation = reviewData.comments.find(
        (conv) => conv.conversationId === conversationId,
      );

      if (existingConversation) {
        const updatedConversation: ReviewSectionConversation = {
          ...existingConversation,
          comments: [
            ...existingConversation.comments,
            {
              message,
              submittedBy: {
                id: currentUser.id,
                name: currentUser.name || 'Unknown',
              },
              createdAt: new Date().toISOString(),
            },
          ],
        };

        const updatedReviewData = {
          ...reviewData,
          comments: reviewData.comments.map((conv) =>
            conv.conversationId === conversationId ? updatedConversation : conv,
          ),
          updatedAt: new Date().toISOString(),
          version: reviewData.version + 1,
        };

        await onReviewDataUpdate(updatedReviewData);
      }
    } catch (error) {
      console.error('Error adding reply:', error);
    }
  };

  return (
    <StyledCard key={sectionId} tone={tone} sx={{ mb: 2, position: 'relative' }}>
      {change?.changed && change.accepted && !isEditing && (
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            bgcolor: isNewSection ? 'success.main' : 'warning.main',
            color: isNewSection ? 'success.contrastText' : 'warning.contrastText',
            px: 1,
            py: 0.5,
            borderRadius: 1,
            fontSize: '0.75rem',
            fontWeight: 'bold',
            zIndex: 1,
          }}
        >
          {isNewSection ? 'New' : 'Modified'}
        </Box>
      )}

      <StyledCardContent>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <StyledTypography tone={tone} variant="h6">
            {getSectionTitle(item)}
          </StyledTypography>

          {!isEditing ? (
            <Stack direction="row" spacing={1}>
              {sectionComments.length > 0 ? (
                <CommentIconComponent
                  conversation={sectionComments[0]}
                  tone={tone}
                  currentUser={currentUser.id}
                  onReply={handleReply}
                />
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {showMinimalReply === sectionId ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 300 }}>
                      <TextField
                        size="small"
                        placeholder="Add your comment..."
                        value={minimalReplyText}
                        onChange={(e) => onMinimalReplyTextChange(e.target.value)}
                        multiline
                        rows={2}
                        autoFocus
                      />
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button size="small" onClick={onMinimalReplyCancel} disabled={submitting}>
                          Cancel
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => onMinimalReplySubmit(sectionId)}
                          disabled={!minimalReplyText.trim() || submitting}
                          startIcon={<CommentIcon />}
                        >
                          {submitting ? 'Adding...' : 'Add Comment'}
                        </Button>
                      </Stack>
                    </Box>
                  ) : (
                    <CommentIconComponent
                      conversation={{
                        conversationId: `new-comment-${sectionId}`,
                        sectionId,
                        comments: [],
                      }}
                      tone={tone}
                      showAddBadge={true}
                      onClick={() => onShowMinimalReply(sectionId)}
                    />
                  )}
                </Box>
              )}
              {change?.changed && (
                <IconButton
                  onClick={() => onRevertToDraft(sectionId)}
                  color="warning"
                  size="small"
                  title="Revert to current draft"
                  disabled={submitting}
                >
                  <UndoIcon />
                </IconButton>
              )}
              <IconButton
                onClick={() => onReplaceComponent(sectionId, displayContent)}
                color="info"
                size="small"
                title="Replace component"
              >
                <ReplaceIcon />
              </IconButton>
              <IconButton
                onClick={() => onViewOriginalComponent(sectionId)}
                color="default"
                size="small"
                title="View original published component"
              >
                <VisibilityIcon />
              </IconButton>
              <IconButton
                onClick={() => onEditStart(sectionId, displayContent)}
                color="primary"
                size="small"
              >
                <EditIcon />
              </IconButton>
            </Stack>
          ) : (
            <Stack direction="row" spacing={1}>
              <IconButton
                onClick={() => onViewOriginalComponent(sectionId)}
                color="default"
                size="small"
                title="View original published component"
              >
                <VisibilityIcon />
              </IconButton>
              <IconButton onClick={() => onEditSave(sectionId, item)} color="primary" size="small">
                <SaveIcon />
              </IconButton>
              <IconButton onClick={onEditCancel} color="secondary" size="small">
                <CancelIcon />
              </IconButton>
            </Stack>
          )}
        </Stack>

        {isEditing ? (
          <Box
            sx={{
              border: '2px dashed',
              borderColor: 'primary.main',
              borderRadius: 1,
              p: 2,
              bgcolor: 'background.default',
            }}
          >
            <ReviewContentEditItemRenderer
              content={currentContent}
              editMode={true}
              tone={tone}
              draftContent={draftContent}
              modelType={modelType}
              onChange={onEditedContentChange}
            />
          </Box>
        ) : (
          <Box>
            <ReviewContentEditItemRenderer
              content={currentContent}
              editMode={false}
              tone={tone}
              draftContent={draftContent}
              modelType={modelType}
            />
          </Box>
        )}
      </StyledCardContent>
    </StyledCard>
  );
}
